import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { ago, dateTime, PLATFORM_LABEL } from '../lib/format.js'
import { links } from '../lib/links.js'
import {
  Badge,
  Card,
  CopyButton,
  Empty,
  Field,
  KeyValue,
  Notice,
  PageHead,
  RowsSkeleton,
  StepList,
  Stepper,
  Ticks,
} from '../components/ui.js'
import {
  ArrowRightIcon,
  BookIcon,
  BoltIcon,
  CardIcon,
  CartIcon,
  ChartIcon,
  CheckCircleIcon,
  CircleIcon,
  CodeIcon,
  CursorIcon,
  ExternalIcon,
  HeadsetIcon,
  KeyIcon,
  LockIcon,
  PaletteIcon,
  PlusIcon,
  RefreshIcon,
  ShieldIcon,
  ShopifyIcon,
  StoreIcon,
  TruckIcon,
  UndoIcon,
  WebhookIcon,
  WhatsAppIcon,
  WordPressIcon,
} from '../components/icons.js'

const WIZARD = ['Install', 'Connect', 'Configure', 'Done']

/** Shared frame: stepper, step list, connection panel, help rail. */
function IntegrationLayout({
  title,
  subtitle,
  tone,
  logo,
  steps,
  step,
  onStep,
  main,
  side,
}: {
  title: string
  subtitle: string
  tone: string
  logo: ReactNode
  steps: Array<{ title: string; description: string }>
  step: number
  onStep: (index: number) => void
  main: ReactNode
  side: ReactNode
}): ReactNode {
  return (
    <>
      <PageHead
        title={title}
        subtitle={subtitle}
        crumbs={[{ label: 'Integrations' }, { label: title }]}
      />

      <Card>
        <Stepper steps={WIZARD} current={step} />
      </Card>

      <div className="grid grid--main">
        <Card
          title={
            <span className="row">
              <span className="feature__icon" style={{ ['--tone' as string]: tone, width: 34, height: 34 }}>
                {logo}
              </span>
              <strong>Connect your store</strong>
            </span>
          }
        >
          <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 240px) minmax(0, 1fr)', gap: 20 }}>
            <StepList steps={steps} current={step} onSelect={onStep} />
            <div className="stack">{main}</div>
          </div>
        </Card>

        <div className="stack">{side}</div>
      </div>
    </>
  )
}

/**
 * The real connection, or an honest empty state.
 *
 * This used to render a sample store, so every merchant saw "Connected —
 * myfashion.myshopify.com" whether or not they had connected anything. It now
 * reads the store the switcher has selected, and says so when there is none.
 */
function ConnectionStatus({ platform }: { platform: 'shopify' | 'woocommerce' | 'custom' }): ReactNode {
  const { shop } = useAuth()

  const { data, isPending } = useQuery({
    queryKey: ['integrations', shop?.id],
    queryFn: () => api<IntegrationInfo>('/v1/integrations'),
    enabled: Boolean(shop?.id),
  })

  if (!shop) {
    return (
      <Card title="Connection status">
        <Empty icon={<StoreIcon size={22} />} title="No store selected">
          Choose a store from the switcher to see its connection.
        </Empty>
      </Card>
    )
  }

  if (isPending) {
    return (
      <Card title="Connection status">
        <RowsSkeleton rows={4} />
      </Card>
    )
  }

  if (!data) return null

  // The selected store may be on a different platform from the page being read.
  const matches = data.platform === platform

  return (
    <Card
      title="Connection status"
      action={
        <Badge tone={data.disconnectedAt ? 'disconnected' : 'connected'} dot>
          {data.disconnectedAt ? 'Disconnected' : 'Connected'}
        </Badge>
      }
    >
      {!matches && (
        <Notice kind="info" title="Different platform">
          The store you have selected is {PLATFORM_LABEL[data.platform] ?? data.platform}. These instructions are for a different
          platform.
        </Notice>
      )}

      <div className="row" style={{ marginBottom: 16 }}>
        <span
          className="store-card__logo"
          style={{ ['--tone' as string]: `var(--${data.platform === 'woocommerce' ? 'woocommerce' : data.platform === 'shopify' ? 'shopify' : 'brand'})` }}
        >
          {data.platform === 'shopify' ? <ShopifyIcon size={20} /> : data.platform === 'woocommerce' ? <WordPressIcon size={20} /> : <CodeIcon size={20} />}
        </span>
        <span className="list__text">
          <strong>{shop.name ?? data.domain}</strong>
          <small>
            <a href={`https://${data.domain}`} target="_blank" rel="noreferrer noopener">
              {data.domain} <ExternalIcon size={11} />
            </a>
          </small>
        </span>
      </div>

      <KeyValue
        rows={[
          { label: 'Connected on', value: data.connectedAt ? dateTime(data.connectedAt) : '—' },
          { label: 'Platform', value: PLATFORM_LABEL[data.platform] ?? data.platform },
          { label: 'Last seen', value: data.lastSeenAt ? ago(data.lastSeenAt) : 'never' },
          ...(data.versions.plugin ? [{ label: 'Plugin', value: data.versions.plugin }] : []),
        ]}
      />
    </Card>
  )
}

/**
 * What the platform told us, and nothing more.
 *
 * The previous version listed six events each with a "last received" time, all
 * invented — nothing records webhook deliveries. Two real dates beat six
 * fabricated ones.
 */
function WebhookStatus(): ReactNode {
  const { shop } = useAuth()

  const { data } = useQuery({
    queryKey: ['integrations', shop?.id],
    queryFn: () => api<IntegrationInfo>('/v1/integrations'),
    enabled: Boolean(shop?.id),
  })

  if (!data) return null

  const registered = data.webhooksRegisteredAt !== null

  return (
    <Card
      title="Webhooks"
      action={
        <Badge tone={registered ? 'ok' : 'warn'} dot>
          {registered ? 'Registered' : 'Not registered'}
        </Badge>
      }
    >
      <KeyValue
        rows={[
          { label: 'Registered', value: data.webhooksRegisteredAt ? dateTime(data.webhooksRegisteredAt) : '—' },
          { label: 'Storefront last seen', value: data.lastSeenAt ? ago(data.lastSeenAt) : 'never' },
        ]}
      />

      <Link to="/integrations/api" className="btn btn--block" style={{ marginTop: 14 }}>
        <WebhookIcon size={15} />
        Webhook URL and keys
      </Link>
    </Card>
  )
}

function HelpCard(): ReactNode {
  return (
    <Card title="Need help?" accent>
      <p className="muted" style={{ fontSize: '.87rem', marginBottom: 14 }}>
        Our support team is here to help you integrate and grow.
      </p>
      <a href={links.support} className="btn btn--primary btn--block">
        <HeadsetIcon size={16} />
        Contact support
      </a>
      <a href={links.whatsapp} className="btn btn--block" style={{ marginTop: 10 }} target="_blank" rel="noreferrer noopener">
        <WhatsAppIcon size={16} />
        Join our WhatsApp channel
      </a>
    </Card>
  )
}

/** A WooCommerce store this account owns, with the key its plugin connects with. */
interface WordPressStore {
  id: string
  name: string | null
  domain: string
  siteUrl: string | null
  storeKey: string | null
  connectedAt: string | null
  lastSeenAt: string | null
  pluginVersion: string | null
}

/**
 * Store keys, issued from this side.
 *
 * The counterpart to the plugin's Connect button: the merchant copies the key
 * shown here into the plugin's "Have a store key instead?" field, and the
 * handshake from the site's own server is what proves the key belongs there.
 * Shown in full because it is the account owner's own credential — masking it
 * would only stop them pasting it where it belongs.
 */
function WordPressStoresCard(): ReactNode {
  const queryClient = useQueryClient()
  const [siteUrl, setSiteUrl] = useState('')
  const [name, setName] = useState('')
  const [freshKey, setFreshKey] = useState<{ domain: string; key: string } | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['wordpress-stores'],
    queryFn: () => api<{ stores: WordPressStore[] }>('/v1/integrations/wordpress/stores'),
  })

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['wordpress-stores'] })
  }

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string; domain: string; key: string }>('/v1/integrations/wordpress/stores', {
        method: 'POST',
        body: { site_url: siteUrl.trim(), name: name.trim() },
      }),
    onSuccess: (result) => {
      setFreshKey({ domain: result.domain, key: result.key })
      setSiteUrl('')
      setName('')
      refresh()
    },
  })

  const rotate = useMutation({
    mutationFn: (id: string) =>
      api<{ id: string; domain: string; key: string }>(`/v1/integrations/wordpress/stores/${id}/rotate`, {
        method: 'POST',
      }),
    onSuccess: (result) => {
      setFreshKey({ domain: result.domain, key: result.key })
      refresh()
    },
  })

  const stores = data?.stores ?? []

  return (
    <Card
      title="Store keys"
      subtitle="Issue a key here, paste it in your WordPress admin — no redirect round trip."
    >
      {isPending && <RowsSkeleton rows={3} />}

      {error && (
        <Notice kind="error" title="Could not load your stores">
          {(error as Error).message}
        </Notice>
      )}

      {freshKey && (
        <Notice kind="ok" title={`Key for ${freshKey.domain}`}>
          <div className="input-group" style={{ marginTop: 8 }}>
            <KeyIcon size={16} />
            <input
              className="input"
              readOnly
              value={freshKey.key}
              onFocus={(event) => event.currentTarget.select()}
            />
            <CopyButton value={freshKey.key} label="Copy store key" />
          </div>
        </Notice>
      )}

      {data &&
        stores.map((store) => (
          <div key={store.id} style={{ padding: '12px 0', borderTop: '1px solid var(--border)' }}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <strong>{store.name || store.domain}</strong>
              <Badge tone={store.lastSeenAt ? 'ok' : 'warn'} dot>
                {store.lastSeenAt ? `seen ${ago(store.lastSeenAt)}` : 'not connected yet'}
              </Badge>
            </div>
            <p className="muted" style={{ fontSize: '.8rem', margin: '4px 0 10px' }}>
              {store.domain}
              {store.pluginVersion ? ` · plugin ${store.pluginVersion}` : ''}
            </p>
            {store.storeKey ? (
              <div className="input-group">
                <KeyIcon size={16} />
                <input
                  className="input"
                  readOnly
                  type="password"
                  value={store.storeKey}
                  onFocus={(event) => {
                    event.currentTarget.type = 'text'
                    event.currentTarget.select()
                  }}
                  onBlur={(event) => {
                    event.currentTarget.type = 'password'
                  }}
                />
                <CopyButton value={store.storeKey} label="Copy store key" />
              </div>
            ) : (
              <p className="muted" style={{ fontSize: '.82rem' }}>No key issued yet.</p>
            )}
            <button
              type="button"
              className="btn btn--sm"
              style={{ marginTop: 10 }}
              disabled={rotate.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Rotate the store key for ${store.domain}? The site stays disconnected until the new key is pasted into the plugin.`,
                  )
                ) {
                  rotate.mutate(store.id)
                }
              }}
            >
              <RefreshIcon size={14} />
              {rotate.isPending ? 'Rotating…' : 'Rotate key'}
            </button>
          </div>
        ))}

      {rotate.error && (
        <Notice kind="error" title="Could not rotate the key" >
          {(rotate.error as Error).message}
        </Notice>
      )}

      <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
        <Field label="Website address" hint="The WordPress site's home URL, e.g. mystore.com or staging.mystore.com.">
          <input
            className="input"
            value={siteUrl}
            onChange={(event) => setSiteUrl(event.target.value)}
            placeholder="mystore.com"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <Field label="Store name (optional)">
          <input
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="My Fashion Store"
            autoComplete="off"
          />
        </Field>
        <button
          type="button"
          className="btn btn--primary btn--block"
          disabled={create.isPending || siteUrl.trim().length < 4}
          onClick={() => create.mutate()}
        >
          <PlusIcon size={16} />
          {create.isPending ? 'Issuing…' : stores.length > 0 ? 'Issue a key for another site' : 'Issue a store key'}
        </button>
        {create.error && (
          <Notice kind="error" title="Could not issue a key">
            {(create.error as Error).message}
          </Notice>
        )}
        <Ticks
          items={[
            'Copy the key into the plugin: Outfalak Checkout → “Have a store key instead?”',
            'The plugin proves the key from your server — that click is what connects the store',
          ]}
        />
      </div>
    </Card>
  )
}

// ── Shopify ─────────────────────────────────────────────────────────────────

interface ShopifyStatus {
  domain: string
  name: string | null
  plan: string | null
  connected: boolean
  /** Whether this deployment has Shopify app credentials at all. */
  configured: boolean
  appUrl: string | null
  installedAt: string | null
  accessTokenExpiresAt: string | null
  scopes: { ok: boolean; granted: string[]; missing: string[]; error: string | null }
  webhooks: { ok: boolean; registered: string[]; missing: string[]; error: string | null }
}

/**
 * What the store's Shopify connection actually looks like.
 *
 * Every row here is read live from Shopify by `/v1/shopify/status`, not from our
 * own columns. The storefront button is deliberately not a row: it lives in the
 * theme app embed, which only the merchant can switch on (script tags are
 * deprecated by Shopify), so no API check could report it honestly.
 */
function ShopifyHealth(): ReactNode {
  const { shop } = useAuth()
  const queryClient = useQueryClient()

  const { data, isPending } = useQuery({
    queryKey: ['shopify-status', shop?.id],
    queryFn: () => api<ShopifyStatus>('/v1/shopify/status'),
    enabled: Boolean(shop?.id),
    // A merchant on this page is usually fixing something; stale health is
    // exactly what they came to stop reading.
    staleTime: 15_000,
  })

  const repair = useMutation({
    mutationFn: () => api('/v1/shopify/repair', { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shopify-status', shop?.id] })
      void queryClient.invalidateQueries({ queryKey: ['integrations', shop?.id] })
    },
  })

  if (!shop) return null

  if (isPending) {
    return (
      <Card title="Shopify health">
        <RowsSkeleton rows={4} />
      </Card>
    )
  }

  if (!data) return null

  if (!data.connected) {
    return (
      <Card title="Shopify health">
        <Empty icon={<ShopifyIcon size={22} />} title="Not connected">
          {data.configured
            ? 'Open Outfalak Checkout from your Shopify admin and it will connect itself.'
            : 'This deployment has no Shopify app configured yet.'}
        </Empty>
      </Card>
    )
  }

  const rows: Array<{ label: string; ok: boolean; detail: string }> = [
    {
      label: 'Permissions',
      ok: data.scopes.ok && data.scopes.missing.length === 0,
      detail: !data.scopes.ok
        ? 'could not be read'
        : data.scopes.missing.length > 0
          ? `missing ${data.scopes.missing.join(', ')}`
          : `${data.scopes.granted.length} granted`,
    },
    {
      label: 'Webhooks',
      ok: data.webhooks.ok && data.webhooks.missing.length === 0,
      detail: !data.webhooks.ok
        ? 'could not be read'
        : data.webhooks.missing.length > 0
          ? `missing ${data.webhooks.missing.join(', ')}`
          : 'all registered',
    },
  ]

  const healthy = rows.every((row) => row.ok)

  return (
    <Card
      title="Shopify health"
      action={
        <Badge tone={healthy ? 'ok' : 'warn'} dot>
          {healthy ? 'Healthy' : 'Needs attention'}
        </Badge>
      }
    >
      <KeyValue
        rows={rows.map((row) => ({
          label: row.label,
          value: (
            <span className="row" style={{ gap: 6 }}>
              {row.ok ? <CheckCircleIcon size={14} /> : <CircleIcon size={14} />}
              {row.detail}
            </span>
          ),
        }))}
      />

      <KeyValue
        rows={[
          { label: 'Store', value: data.name ?? data.domain },
          ...(data.plan ? [{ label: 'Shopify plan', value: data.plan }] : []),
          { label: 'Connected on', value: data.installedAt ? dateTime(data.installedAt) : '—' },
        ]}
      />

      {!healthy && (
        <Notice kind="warn" title="Something on the store is missing">
          Re-running setup re-registers the webhooks. It is safe to run more than once. The Buy Now button itself
          lives in the theme app embed — switch it on under Online Store → Themes → Customise → App embeds.
        </Notice>
      )}

      <button
        type="button"
        className="btn btn--block"
        style={{ marginTop: 12 }}
        onClick={() => repair.mutate()}
        disabled={repair.isPending}
      >
        <RefreshIcon size={15} />
        {repair.isPending ? 'Re-running setup…' : 'Re-run setup'}
      </button>

      {repair.isError && (
        <Notice kind="error" title="That did not work">
          {(repair.error as Error).message}
        </Notice>
      )}

      {data.appUrl && (
        <a
          href={data.appUrl}
          className="btn btn--block"
          style={{ marginTop: 10 }}
          target="_blank"
          rel="noreferrer noopener"
        >
          <ExternalIcon size={14} />
          Open in Shopify admin
        </a>
      )}
    </Card>
  )
}

/**
 * The one button on this page, and what it should say.
 *
 * It used to be a fixed link to `apps.shopify.com` — the store front page, not
 * this app — shown to every merchant including the ones already connected. What
 * it should say depends on two facts the API knows and the browser does not:
 * whether this deployment has a Shopify app at all, and whether the selected
 * store is already on it.
 *
 * Nothing here fabricates a listing URL. An unlisted or custom-distributed app
 * has no App Store page, and its merchants install from a link an operator sends
 * them — so that case is named rather than papered over with a link that 404s.
 */
function ShopifyInstallAction(): ReactNode {
  const { shop } = useAuth()

  const { data: deployment } = useQuery({
    queryKey: ['shopify-app'],
    queryFn: () => api<{ configured: boolean; handle: string | null }>('/v1/shopify/app'),
    staleTime: 5 * 60_000,
  })

  const { data: status } = useQuery({
    queryKey: ['shopify-status', shop?.id],
    queryFn: () => api<ShopifyStatus>('/v1/shopify/status'),
    enabled: Boolean(shop?.id),
    staleTime: 15_000,
  })

  if (deployment && !deployment.configured) {
    return (
      <Notice kind="warn" title="Shopify is not set up on this deployment">
        No Shopify app credentials are configured, so no store can connect yet. An operator needs to add them
        before this page can do anything.
      </Notice>
    )
  }

  if (status?.connected) {
    return (
      <>
        <Notice kind="ok" title={`${status.name ?? status.domain} is connected`}>
          Nothing more to install. Outfalak Checkout is live on this store — the panel on the right shows what it
          registered.
        </Notice>

        {status.appUrl && (
          <a
            href={status.appUrl}
            className="btn btn--shopify btn--lg btn--block"
            target="_blank"
            rel="noreferrer noopener"
          >
            <ShopifyIcon size={18} />
            Open Outfalak Checkout in Shopify
          </a>
        )}
      </>
    )
  }

  return <ShopifyStoreForm />
}

/**
 * The merchant naming their own store.
 *
 * The address is completed and validated by the API rather than here, and the
 * install link is built there too — the client id belongs to whichever Shopify
 * app serves this merchant, which the browser has no way to know. So this field
 * does no cleverness: it collects a string and hands it over.
 */
function ShopifyStoreForm(): ReactNode {
  const [shop, setShop] = useState('')

  const start = useMutation({
    mutationFn: (value: string) =>
      api<{ shop: string; url: string }>('/v1/shopify/install-url', { query: { shop: value } }),
    onSuccess: ({ url }) => {
      // A full navigation, not a new tab. Shopify's approval screen ends by
      // opening the app inside the merchant's admin, so the journey deliberately
      // leaves this page rather than stranding it behind one.
      window.location.assign(url)
    },
  })

  const error = start.error instanceof ApiError ? start.error : null

  return (
    <>
      <Notice kind="ok">
        <Ticks
          items={[
            'Approve the permissions once — Shopify handles the rest',
            'No theme edits — the Buy Now button installs automatically',
            'Orders sync back into your Shopify admin',
          ]}
        />
      </Notice>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (shop.trim()) start.mutate(shop.trim())
        }}
        className="stack"
      >
        <Field
          label="Your Shopify store address"
          hint="Just the handle is enough — we add .myshopify.com for you."
        >
          <input
            type="text"
            value={shop}
            onChange={(event) => setShop(event.target.value)}
            placeholder="your-store.myshopify.com"
            autoComplete="off"
            spellCheck={false}
            disabled={start.isPending}
          />
        </Field>

        <button
          type="submit"
          className="btn btn--shopify btn--lg btn--block"
          disabled={start.isPending || shop.trim() === ''}
        >
          <ShopifyIcon size={18} />
          {start.isPending ? 'Taking you to Shopify…' : 'Continue to Shopify'}
        </button>
      </form>

      {error && (
        <Notice kind="error" title="Could not start the install">
          {error.fields?.shop?.[0] ?? error.message}
        </Notice>
      )}

      <p
        className="muted"
        style={{ textAlign: 'center', fontSize: '.8rem', display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'center' }}
      >
        <LockIcon size={13} />
        Shopify asks for your approval — we never see your password
      </p>

      <Notice kind="info" title="Already installed it from Shopify?">
        Open Outfalak Checkout once from <strong>Shopify admin → Apps</strong> and it connects itself. You do not
        need this form.
      </Notice>
    </>
  )
}

export function ShopifyIntegrationPage(): ReactNode {
  const [step, setStep] = useState(0)

  return (
    <IntegrationLayout
      title="Shopify"
      subtitle="Connect your Shopify store with Outfalak Checkout in a few easy steps."
      tone="var(--shopify)"
      logo={<ShopifyIcon size={18} />}
      step={step}
      onStep={setStep}
      steps={[
        { title: 'Install app', description: 'Install Outfalak Checkout from the Shopify App Store.' },
        { title: 'Connect store', description: 'Authorise and connect your store.' },
        { title: 'Configure settings', description: 'Set up checkout and payment.' },
        { title: 'Done', description: 'Start receiving orders.' },
      ]}
      main={
        <>
          <div>
            <h3>Step {step + 1}: {['Install the Outfalak Checkout app', 'Authorise your store', 'Configure your checkout', 'You are all set'][step]}</h3>
            <p className="muted" style={{ fontSize: '.87rem', marginTop: 4 }}>
              {
                [
                  'Install our official app from the Shopify App Store to connect your store.',
                  'Shopify will ask you to approve the permissions Outfalak Checkout needs.',
                  'Choose your gateway, COD rules and which checkout Shopify should use.',
                  'Orders placed through Outfalak Checkout will sync back to Shopify automatically.',
                ][step]
              }
            </p>
          </div>

          <Notice kind="ok">
            <Ticks items={['One-click installation', 'Secure & official Shopify app', 'Automatic checkout integration', '24/7 dedicated support']} />
          </Notice>

          <ShopifyInstallAction />
        </>
      }
      side={
        <>
          <ConnectionStatus platform="shopify" />
          <ShopifyHealth />
          <Card title="What's included?">
            <div className="feature-grid">
              <Feature icon={<CartIcon size={16} />} title="One-page checkout" description="Replaces the default checkout with a higher-converting one." tone="var(--brand)" />
              <Feature icon={<BoltIcon size={16} />} title="Buy Now button" description="Fast checkout straight from product pages." tone="var(--warn)" />
              <Feature icon={<CardIcon size={16} />} title="UPI, cards, netbanking" description="Every major Indian payment method." tone="var(--ok)" />
              <Feature icon={<RefreshIcon size={16} />} title="Order sync" description="Orders flow back into Shopify automatically." tone="var(--info)" />
              <Feature icon={<TruckIcon size={16} />} title="COD with smart rules" description="Limits, fees and OTP verification." tone="var(--accent)" />
              <Feature icon={<ChartIcon size={16} />} title="Real-time analytics" description="Track performance and conversion live." tone="var(--brand)" />
              <Feature icon={<UndoIcon size={16} />} title="Abandoned recovery" description="Win back lost orders over WhatsApp." tone="var(--bad)" />
              <Feature icon={<PaletteIcon size={16} />} title="Brand customisation" description="Match the checkout to your brand." tone="var(--woocommerce)" />
            </div>
          </Card>
          <HelpCard />
        </>
      }
    />
  )
}

function Feature({ icon, title, description, tone }: { icon: ReactNode; title: string; description: string; tone: string }): ReactNode {
  return (
    <div className="feature" style={{ ['--tone' as string]: tone }}>
      <span className="feature__icon">{icon}</span>
      <span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
    </div>
  )
}

// ── WordPress / WooCommerce ─────────────────────────────────────────────────

export function WordPressIntegrationPage(): ReactNode {
  const [step, setStep] = useState(1)

  return (
    <IntegrationLayout
      title="WordPress / WooCommerce"
      subtitle="Connect your WordPress website with Outfalak Checkout in a few easy steps."
      tone="var(--woocommerce)"
      logo={<WordPressIcon size={18} />}
      step={step}
      onStep={setStep}
      steps={[
        { title: 'Install plugin', description: 'Install the Outfalak Checkout plugin.' },
        { title: 'Connect website', description: 'Connect your WordPress site.' },
        { title: 'Configure settings', description: 'Set up checkout & payment.' },
        { title: 'Done', description: 'Start receiving orders.' },
      ]}
      main={
        <>
          <div>
            <h3>Step {step + 1}: {['Install the Outfalak Checkout plugin', 'Connect your WordPress website', 'Configure your checkout', 'You are all set'][step]}</h3>
            <p className="muted" style={{ fontSize: '.87rem', marginTop: 4 }}>
              {
                [
                  'Install it free from WordPress.org.',
                  'Connect your website using API credentials, or let us configure the plugin automatically.',
                  'Enable the checkout, choose a gateway and set your COD rules.',
                  'Orders placed through Outfalak Checkout will sync back to WooCommerce automatically.',
                ][step]
              }
            </p>
          </div>

          <a
            href={links.wordpressOrgPlugin}
            className="btn btn--primary btn--lg btn--block"
            target="_blank"
            rel="noopener"
          >
            <ExternalIcon size={18} />
            Get it from WordPress.org
          </a>

          <ol className="list list--plain">
            {[
              'Open the WordPress.org page above, or search "Outfalak Checkout" in Plugins → Add New',
              'Click Install, then Activate',
              'Outfalak Checkout appears in your WordPress sidebar',
            ].map((line, index) => (
              <li key={line} style={{ paddingLeft: 0, alignItems: 'flex-start' }}>
                <span className="stepper__num" style={{ width: 24, height: 24, fontSize: '.75rem' }}>
                  {index + 1}
                </span>
                <span className="list__text">
                  <strong style={{ fontWeight: 500 }}>{line}</strong>
                </span>
              </li>
            ))}
          </ol>


          <Notice kind="info" title="After activating">
            The plugin asks for your store key. Issue one in the <strong>Store keys</strong> card on this page and
            paste it into the plugin's “Have a store key instead?” field — that is what connects the store to this
            dashboard. Or press the plugin's <strong>Connect to Outfalak Checkout</strong> button for the automatic
            round trip instead.
          </Notice>

          <p className="muted" style={{ textAlign: 'center', fontSize: '.8rem', display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'center' }}>
            <LockIcon size={13} />
            Secure &amp; read-only access
          </p>

        </>
      }
      side={
        <>
          <ConnectionStatus platform="woocommerce" />
          <WordPressStoresCard />
          <WebhookStatus />
          <Card title="Before you start">
            <Ticks
              items={[
                'WordPress 6.2 or higher',
                'WooCommerce 7.0 or higher',
                'PHP 8.1 or higher',
                'SSL certificate (HTTPS)',
                'Admin access to your website',
              ]}
            />
          </Card>
          <HelpCard />
        </>
      }
    />
  )
}

// ── Custom website ──────────────────────────────────────────────────────────

/*
 * The real embed, and the real way to drive it.
 *
 * What stood here described an SDK that does not exist: a script at
 * `/sdk/v2.js`, which 404s, calling `window.OutfalakCheckout.init({publicKey})`,
 * which nothing defines. A merchant who copied it got a script tag that fetched
 * nothing and, had it loaded, a method that was not there.
 *
 * The shipped file configures itself from the tag — `data-outfalak-key` is what
 * it looks for, and it refuses to start without one — and reads each line item
 * off the button's own data attributes, falling back to the nearest ancestor
 * carrying `data-outfalak-product` so a product grid can describe the product
 * once. Verified against the served file, not from memory.
 */
const snippetFor = (key: string | null): string => `<script src="${links.sdk}"
        data-outfalak-key="${key ?? 'pk_live_xxxxxxxxxxxx'}" defer></script>

<button data-outfalak-buy
        data-outfalak-sku="TSHIRT-M"
        data-outfalak-name="Cotton T-Shirt"
        data-outfalak-price="799">Buy Now</button>`

/*
 * For a site whose cart is built in JavaScript rather than described in markup.
 *
 * `ts` and `sig` are optional and come from the merchant's own server: the
 * public key authorises opening a checkout, never a price. Prices are recomputed
 * server-side from the catalogue before anyone is charged, so an edited
 * `data-outfalak-price` buys nothing.
 */
const SNIPPET_JS = `window.Outfalak.checkout({
  items: [{ sku: 'TSHIRT-M', name: 'Cotton T-Shirt', price: 799, quantity: 1 }],
})`

interface CustomStore {
  id: string
  name: string | null
  domain: string
  siteUrl: string | null
  publicKey: string | null
  lastSeenAt: string | null
}

/**
 * Where a custom-website store and its public key actually get created.
 *
 * The wizard at "Add store" only points here — it cannot provision anything
 * itself, because there is no plugin or OAuth round trip for a custom site to
 * drive it. This card is the whole of that provisioning: name a site, get a
 * key, paste it into the `<script>` tag above.
 */
function useCustomStores() {
  return useQuery({
    queryKey: ['custom-stores'],
    queryFn: () => api<{ stores: CustomStore[] }>('/v1/integrations/custom/stores'),
  })
}

/**
 * A new store also has to reach the store switcher, the Stores page and — for
 * a first store — the session's current shop, or every per-store screen reads
 * "No store selected" until a hard refresh.
 */
function useRefreshCustomStores(): () => void {
  const queryClient = useQueryClient()
  const { refresh: refreshSession } = useAuth()

  return () => {
    void queryClient.invalidateQueries({ queryKey: ['custom-stores'] })
    void queryClient.invalidateQueries({ queryKey: ['stores'] })
    void refreshSession()
  }
}

/** Website address + name → a shop row and its public key. Leads the page: it is step one. */
function AddCustomSiteForm({ hasStores }: { hasStores: boolean }): ReactNode {
  const refresh = useRefreshCustomStores()
  const [siteUrl, setSiteUrl] = useState('')
  const [name, setName] = useState('')

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string; domain: string; key: string }>('/v1/integrations/custom/stores', {
        method: 'POST',
        body: { site_url: siteUrl.trim(), name: name.trim() },
      }),
    onSuccess: () => {
      setSiteUrl('')
      setName('')
      refresh()
    },
  })

  return (
    <div style={{ paddingBottom: 18, borderBottom: '1px solid var(--border)' }}>
      <div className="grid grid--2">
        <Field label="Website address" hint="The site the checkout will run on, e.g. mystore.com.">
          <input
            className="input"
            value={siteUrl}
            onChange={(event) => setSiteUrl(event.target.value)}
            placeholder="mystore.com"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <Field label="Store name (optional)">
          <input
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="My Fashion Store"
            autoComplete="off"
          />
        </Field>
      </div>
      <button
        type="button"
        className="btn btn--primary"
        disabled={create.isPending || siteUrl.trim().length < 4}
        onClick={() => create.mutate()}
      >
        <PlusIcon size={16} />
        {create.isPending ? 'Issuing…' : hasStores ? 'Add another site & get key' : 'Create store & get key'}
      </button>

      {create.data && (
        <Notice kind="ok" title={`Key for ${create.data.domain}`}>
          <div className="input-group" style={{ marginTop: 8 }}>
            <KeyIcon size={16} />
            <input className="input" readOnly value={create.data.key} onFocus={(event) => event.currentTarget.select()} />
            <CopyButton value={create.data.key} label="Copy public key" />
          </div>
        </Notice>
      )}

      {create.error && (
        <Notice kind="error" title="Could not create the store">
          {(create.error as Error).message}
        </Notice>
      )}
    </div>
  )
}

function CustomStoresCard(): ReactNode {
  const refresh = useRefreshCustomStores()
  const [freshKey, setFreshKey] = useState<{ domain: string; key: string } | null>(null)

  const { data, isPending, error } = useCustomStores()

  const rotate = useMutation({
    mutationFn: (id: string) =>
      api<{ id: string; domain: string; key: string }>(`/v1/integrations/custom/stores/${id}/rotate`, {
        method: 'POST',
      }),
    onSuccess: (result) => {
      setFreshKey({ domain: result.domain, key: result.key })
      refresh()
    },
  })

  const stores = data?.stores ?? []

  return (
    <Card title="Store keys" subtitle="Your custom sites and the public key each one's SDK needs.">
      {isPending && <RowsSkeleton rows={3} />}

      {error && (
        <Notice kind="error" title="Could not load your stores">
          {(error as Error).message}
        </Notice>
      )}

      {freshKey && (
        <Notice kind="ok" title={`Key for ${freshKey.domain}`}>
          <div className="input-group" style={{ marginTop: 8 }}>
            <KeyIcon size={16} />
            <input className="input" readOnly value={freshKey.key} onFocus={(event) => event.currentTarget.select()} />
            <CopyButton value={freshKey.key} label="Copy public key" />
          </div>
        </Notice>
      )}

      {data &&
        stores.map((store) => (
          <div key={store.id} style={{ padding: '12px 0', borderTop: '1px solid var(--border)' }}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <strong>{store.name || store.domain}</strong>
              <Badge tone={store.lastSeenAt ? 'ok' : 'warn'} dot>
                {store.lastSeenAt ? `seen ${ago(store.lastSeenAt)}` : 'not connected yet'}
              </Badge>
            </div>
            <p className="muted" style={{ fontSize: '.8rem', margin: '4px 0 10px' }}>
              {store.domain}
            </p>
            {store.publicKey ? (
              <div className="input-group">
                <KeyIcon size={16} />
                <input className="input" readOnly value={store.publicKey} onFocus={(event) => event.currentTarget.select()} />
                <CopyButton value={store.publicKey} label="Copy public key" />
              </div>
            ) : (
              <p className="muted" style={{ fontSize: '.82rem' }}>No key issued yet.</p>
            )}
            <button
              type="button"
              className="btn btn--sm"
              style={{ marginTop: 10 }}
              disabled={rotate.isPending}
              onClick={() => {
                if (
                  window.confirm(`Rotate the public key for ${store.domain}? The embed stops working until the new key replaces it.`)
                ) {
                  rotate.mutate(store.id)
                }
              }}
            >
              <RefreshIcon size={14} />
              {rotate.isPending ? 'Rotating…' : 'Rotate key'}
            </button>
          </div>
        ))}

      {data && stores.length === 0 && (
        <p className="muted" style={{ fontSize: '.85rem' }}>
          No sites yet — add your website address under "Connect your store".
        </p>
      )}

      {rotate.error && (
        <Notice kind="error" title="Could not rotate the key">
          {(rotate.error as Error).message}
        </Notice>
      )}
    </Card>
  )
}

export function CustomWebsiteIntegrationPage(): ReactNode {
  const [step, setStep] = useState(0)
  const { data } = useCustomStores()
  // Newest site first (the list is ordered by id), so a store just added here
  // is the one whose key lands in the snippet.
  const snippet = snippetFor([...(data?.stores ?? [])].reverse().find((store) => store.publicKey)?.publicKey ?? null)

  return (
    <IntegrationLayout
      title="Custom Website"
      subtitle="Integrate Outfalak Checkout into any site with the JavaScript SDK or the REST API."
      tone="var(--brand)"
      logo={<CodeIcon size={18} />}
      step={step}
      onStep={setStep}
      steps={[
        { title: 'Get your keys', description: 'Add your website — the key is created for it automatically.' },
        { title: 'Drop in the SDK', description: 'Add one script tag to your site.' },
        { title: 'Wire the button', description: 'Call open() with your cart.' },
        { title: 'Done', description: 'Start receiving orders.' },
      ]}
      main={
        <>
          <div>
            <h3>Step {step + 1}: {['Get your API keys', 'Add the SDK to your site', 'Open the checkout', 'You are all set'][step]}</h3>
            <p className="muted" style={{ fontSize: '.87rem', marginTop: 4 }}>
              {
                [
                  'Enter your website address and store name — the public key is created automatically and filled into the snippet below.',
                  'One script tag, deferred, on every page that can start a checkout.',
                  'Call OutfalakCheckout.open() with the cart. Prices are re-verified server-side.',
                  'Orders appear in this dashboard the moment they are placed.',
                ][step]
              }
            </p>
          </div>

          {step === 0 && <AddCustomSiteForm hasStores={(data?.stores.length ?? 0) > 0} />}

          <div style={{ position: 'relative' }}>
            <pre
              style={{
                margin: 0,
                padding: 16,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--surface-3)',
                border: '1px solid var(--border)',
                overflowX: 'auto',
                fontSize: '.8rem',
                lineHeight: 1.6,
              }}
            >
              <code>{snippet}</code>
            </pre>
            <span style={{ position: 'absolute', top: 8, right: 8 }}>
              <CopyButton value={snippet} label="Copy snippet" />
            </span>
          </div>

          <p className="muted" style={{ fontSize: '.87rem', marginTop: 18 }}>
            Building the cart in JavaScript instead? Call it directly:
          </p>

          <pre
            style={{
              margin: 0,
              padding: 16,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--surface-3)',
              border: '1px solid var(--border)',
              overflowX: 'auto',
              fontSize: '.8rem',
              lineHeight: 1.6,
            }}
          >
            <code>{SNIPPET_JS}</code>
          </pre>

          <Notice kind="warn" title="Never ship your secret key to the browser">
            Anything in a <code>&lt;script&gt;</code> tag is public. Server-to-server calls use the secret key; the embed uses
            the public one — and it authorises opening a checkout, not setting a price.
          </Notice>

          {/* The overlay is opt-in, not the default. Inside a cross-origin
              iframe the checkout's cookies are third-party, which Safari drops
              outright — so the shipped default is a top-level navigation, and
              saying otherwise here would have merchants expecting a modal that
              only appears once they turn it on. */}
          <Ticks items={['Works with HTML, PHP, React, Next.js or anything else', 'Opens as a full-page checkout; the overlay is opt-in', 'Webhooks tell your server when an order is paid']} />
        </>
      }
      side={
        <>
          <ConnectionStatus platform="custom" />
          <CustomStoresCard />
          <Card title="What you get">
            <div className="feature-grid">
              <Feature icon={<CursorIcon size={16} />} title="Overlay checkout" description="No redirect, no page reload." tone="var(--brand)" />
              <Feature icon={<ShieldIcon size={16} />} title="Server-side pricing" description="Totals are recomputed before charging." tone="var(--ok)" />
              <Feature icon={<WebhookIcon size={16} />} title="Webhooks" description="Order created, paid, cancelled, refunded." tone="var(--accent)" />
              <Feature icon={<CardIcon size={16} />} title="All payment methods" description="UPI, cards, netbanking, wallets, COD." tone="var(--info)" />
            </div>
          </Card>
          <HelpCard />
        </>
      }
    />
  )
}

// ── API & webhooks ──────────────────────────────────────────────────────────

/** What `/v1/integrations` answers with. */
interface IntegrationInfo {
  platform: string
  domain: string
  siteUrl: string | null
  publicKey: string | null
  webhookUrl: string | null
  allowedOrigins: string[]
  connectedAt: string | null
  disconnectedAt: string | null
  lastSeenAt: string | null
  webhooksRegisteredAt: string | null
  versions: { api: string | null; wordpress: string | null; woocommerce: string | null; plugin: string | null }
  scopes: string[]
}

/**
 * The store's real wiring: its public key, its inbound webhook URL, and what
 * the plugin last reported.
 *
 * The key is shown in full on purpose — it is public by construction, ships to
 * the shopper's browser, and is protected by the Origin allowlist below rather
 * than by secrecy. Masking it would only stop the merchant pasting it where it
 * belongs.
 */
export function ApiWebhooksPage(): ReactNode {
  const { shop } = useAuth()
  const queryClient = useQueryClient()
  const [origins, setOrigins] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const { data, isPending, error } = useQuery({
    queryKey: ['integrations', shop?.id],
    queryFn: () => api<IntegrationInfo>('/v1/integrations'),
    enabled: Boolean(shop?.id),
  })

  const save = useMutation({
    mutationFn: (list: string[]) => api('/v1/integrations/origins', { method: 'PUT', body: { allowedOrigins: list } }),
    onSuccess: () => {
      setOrigins(null)
      setSaved(true)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: ['integrations', shop?.id] })
    },
  })

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          API keys and webhooks are per store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  const originText = origins ?? data?.allowedOrigins.join('\n') ?? ''
  const dirty = origins !== null && origins !== (data?.allowedOrigins.join('\n') ?? '')

  return (
    <>
      <PageHead
        title="API &amp; webhooks"
        subtitle="Your public key, your webhook URL, and where checkouts may start from."
        crumbs={[{ label: 'Integrations' }, { label: 'API & webhooks' }]}
      />

      {saved && <Notice kind="ok" title="Saved">The allowlist applies to new checkouts immediately.</Notice>}

      {save.error && (
        <Notice kind="error" title="Could not save">
          {(save.error as Error).message}
        </Notice>
      )}

      {error && (
        <Card>
          <Empty error title="Could not load your integration details">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && (
        <Card>
          <RowsSkeleton rows={6} />
        </Card>
      )}

      {data && (
        <div className="grid grid--main">
          <div className="stack">
            <Card title="Public key" subtitle="Safe to ship to the browser — this is what opens a checkout.">
              {data.publicKey ? (
                <div className="input-group">
                  <KeyIcon size={16} />
                  <input className="input" readOnly value={data.publicKey} onFocus={(e) => e.currentTarget.select()} />
                  <CopyButton value={data.publicKey} label="Copy public key" />
                </div>
              ) : (
                <Notice kind="warn" title="No public key yet">
                  This store has no key. It is issued when a custom storefront connects.
                </Notice>
              )}

              <p className="muted" style={{ fontSize: '.82rem', marginTop: 12 }}>
                <ShieldIcon size={13} style={{ verticalAlign: '-2px' }} /> What protects this key is the origin allowlist below,
                not secrecy. A checkout started from an origin that is not listed is refused.
              </p>
            </Card>

            <Card title="Webhook URL" subtitle="Where your platform sends order events.">
              {data.webhookUrl ? (
                <div className="input-group">
                  <WebhookIcon size={16} />
                  <input className="input" readOnly value={data.webhookUrl} onFocus={(e) => e.currentTarget.select()} />
                  <CopyButton value={data.webhookUrl} label="Copy webhook URL" />
                </div>
              ) : (
                <p className="muted">No webhook URL for this store.</p>
              )}

              {/* No delivery log: nothing records webhook attempts, so rather
                  than invent "last received 2 min ago", this reports only what
                  the platform actually told us. */}
              <KeyValue
                rows={[
                  {
                    label: 'Webhooks registered',
                    value: data.webhooksRegisteredAt ? dateTime(data.webhooksRegisteredAt) : <span className="faint">not registered</span>,
                  },
                  {
                    label: 'Storefront last seen',
                    value: data.lastSeenAt ? ago(data.lastSeenAt) : <span className="faint">never</span>,
                  },
                ]}
              />

              <Notice kind="info" title="No delivery history yet">
                Individual webhook attempts are not recorded on this deployment, so there is nothing to list here. The two dates
                above are what your platform reported.
              </Notice>
            </Card>

            <Card
              title="Allowed origins"
              subtitle="Extra sites that may start a checkout. Your own domain is always allowed."
              action={
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={!dirty || save.isPending}
                  onClick={() => save.mutate(originText.split('\n').map((line) => line.trim()).filter(Boolean))}
                >
                  {save.isPending ? 'Saving…' : 'Save'}
                </button>
              }
            >
              <Field label="One origin per line" hint="Staging copies, landing pages, the www form — anything that is not your main domain.">
                <textarea
                  className="textarea"
                  value={originText}
                  placeholder={`https://staging.${data.domain}\nhttps://www.${data.domain}`}
                  onChange={(e) => setOrigins(e.target.value)}
                />
              </Field>

              {dirty && (
                <Notice kind="warn" title="Unsaved changes">
                  Checkouts keep using the current allowlist until you press Save.
                </Notice>
              )}
            </Card>
          </div>

          <div className="stack">
            <Card title="This store">
              <KeyValue
                rows={[
                  { label: 'Platform', value: PLATFORM_LABEL[data.platform] ?? data.platform },
                  { label: 'Domain', value: data.domain },
                  ...(data.siteUrl ? [{ label: 'Site URL', value: data.siteUrl }] : []),
                  {
                    label: 'Connected',
                    value: data.connectedAt ? dateTime(data.connectedAt).split(',')[0] : <span className="faint">—</span>,
                  },
                  {
                    label: 'Status',
                    value: data.disconnectedAt ? <Badge tone="bad">Disconnected</Badge> : <Badge tone="ok">Connected</Badge>,
                  },
                ]}
              />
            </Card>

            <Card title="Versions" subtitle="Reported by the storefront itself.">
              <KeyValue
                rows={[
                  { label: 'API version', value: data.versions.api ?? '—' },
                  { label: 'Plugin', value: data.versions.plugin ?? <span className="faint">not reported</span> },
                  { label: 'WordPress', value: data.versions.wordpress ?? <span className="faint">not reported</span> },
                  { label: 'WooCommerce', value: data.versions.woocommerce ?? <span className="faint">not reported</span> },
                ]}
              />
            </Card>

            {data.scopes.length > 0 && (
              <Card title="Granted scopes">
                <div className="row" style={{ flexWrap: 'wrap', gap: 7 }}>
                  {data.scopes.map((scope) => (
                    <Badge key={scope} tone="outline">
                      {scope}
                    </Badge>
                  ))}
                </div>
              </Card>
            )}

            <Card title="Events we send" flush>
              <ul className="list">
                {['order.created', 'order.paid', 'order.cancelled', 'order.refunded', 'checkout.abandoned', 'wallet.low'].map(
                  (event) => (
                    <li key={event}>
                      <WebhookIcon size={16} style={{ color: 'var(--ink-faint)' }} />
                      <span className="list__text">
                        <code>{event}</code>
                      </span>
                      <CopyButton value={event} label={`Copy ${event}`} />
                    </li>
                  ),
                )}
              </ul>
            </Card>
          </div>
        </div>
      )}
    </>
  )
}
